import type { GeoJsonProperties } from 'geojson'
import { useMemo } from 'react'
import Supercluster from 'supercluster'
import { useGoogleMapViewport } from '../google'
import { CLUSTER_ZOOM_LEVEL } from '~/utils'
import {
  isListingClusterFeature,
  type ClusterOrListingPointFeature,
  type ListingClusterFeature,
  type ListingPointFeature,
  type ListingPointProperties,
} from './listingPointFeatures'

export const GOOGLE_LISTING_CLUSTER_RADIUS_PX = 48
export const GOOGLE_LISTING_CLUSTER_MIN_POINTS = 6

export function createListingClusterIndex(points: ListingPointFeature[]) {
  return new Supercluster<ListingPointProperties, GeoJsonProperties>({
    maxZoom: CLUSTER_ZOOM_LEVEL,
    minPoints: GOOGLE_LISTING_CLUSTER_MIN_POINTS,
    radius: GOOGLE_LISTING_CLUSTER_RADIUS_PX,
  }).load(points)
}

export function useListingClusters({
  points,
  selectedListingId,
}: {
  points: ListingPointFeature[]
  selectedListingId?: string | null
}) {
  const viewport = useGoogleMapViewport()
  const clusterIndex = useMemo(() => createListingClusterIndex(points), [points])

  const clusteredFeatures = useMemo<ClusterOrListingPointFeature[]>(() => {
    if (!viewport) return points

    return clusterIndex.getClusters(viewport.bbox, viewport.zoom)
  }, [clusterIndex, points, viewport])

  const clusterFeatures = useMemo<ListingClusterFeature[]>(
    () => clusteredFeatures.filter(isListingClusterFeature),
    [clusteredFeatures]
  )

  const listingFeatures = useMemo<ListingPointFeature[]>(
    () =>
      clusteredFeatures
        .filter(
          (feature): feature is ListingPointFeature =>
            !isListingClusterFeature(feature)
        )
        .filter((feature) => feature.properties._id !== selectedListingId),
    [clusteredFeatures, selectedListingId]
  )

  const selectedFeatures = useMemo<ListingPointFeature[]>(
    () =>
      selectedListingId
        ? points.filter(
            (feature) => feature.properties._id === selectedListingId
          )
        : [],
    [points, selectedListingId]
  )

  return {
    clusteredFeatures,
    clusterFeatures,
    clusterIndex,
    listingFeatures,
    selectedFeatures,
    viewport,
  }
}
